/**
 * Where the cards sit, and how that survives a reload.
 *
 * The layout is **per device**, in `localStorage`. An arrangement is a property of the
 * screen you made it on — an ultrawide's three columns mean nothing on a phone, and syncing
 * one to the other would be actively wrong. It is also why this never goes near the API.
 *
 * ## Stored per column count, not folded
 *
 * The column count comes from the width (1, 2 or 3), so a stored layout has to answer "where
 * does this card go" at every count. Two ways to do that: keep one arrangement and fold it
 * — merge column 3 into column 2 when the window narrows — or keep one arrangement per
 * count.
 *
 * Folding sounds tidier and behaves badly. Drag a card at two columns and it is ambiguous
 * which three-column slot you meant, so the wide layout you carefully built gets edited by a
 * drag you did on a laptop. Keeping them separate matches what someone actually means: "my
 * desk layout" and "my laptop layout" are different opinions, and editing one should not
 * disturb the other.
 */

/** Every card that can be placed. Stable strings — they are written to storage. */
export type SectionId =
  // Briefing
  | "add"
  | "activity"
  | "wins"
  | "notice"
  | "gone-quiet"
  | "first-run"
  | "starred"
  | "recurring"
  | "inbox"
  | "tasks"
  | "notes"
  | "blackjack"
  // Areas & projects
  | "areas"
  | "projects"
  // Settings
  | "settings-display"
  | "settings-capture"
  | "settings-account"
  | "settings-sessions"
  | "settings-invites"
  | "settings-tokens"
  // Models
  | "models-configs"
  | "models-slots"
  | "models-prompts"
  | "models-context";

/**
 * How many columns are showing.
 *
 * A plain number rather than `1 | 2 | 3`, because the count can now be set by hand and the
 * manual ceiling is deliberately higher than sensible. Someone who wants eight columns on a
 * laptop is entitled to find out what that looks like; it is one click back.
 */
export type ColumnCount = number;

/**
 * Every card a page can hold, and its name.
 *
 * Needed because most of the briefing is conditional: notice, first run, starred and the
 * inbox are absent more often than not, and a card you cannot see is a card you cannot
 * place. Arrange mode renders the absent ones as placeholders so they can be positioned
 * before they exist — otherwise the only way to decide where the inbox goes is to first
 * arrange for something to be in it.
 */
export const SECTION_LABELS: Record<SectionId, string> = {
  add: "Add a task",
  activity: "Recent activity",
  wins: "Wins",
  notice: "Notice",
  "gone-quiet": "Gone quiet",
  "first-run": "First run",
  starred: "Starred",
  recurring: "Routines",
  inbox: "Inbox",
  tasks: "Tasks",
  notes: "Notes",
  blackjack: "Blackjack",
  areas: "Areas",
  projects: "Projects",
  "settings-display": "Display",
  "settings-capture": "Capture tier",
  "settings-account": "Account",
  "settings-sessions": "Sessions",
  "settings-invites": "Invites",
  "settings-tokens": "Tokens",
  "models-configs": "Model connections",
  "models-slots": "Model assignment",
  "models-prompts": "Prompts",
  "models-context": "Context",
};

/**
 * `PAGE_SECTIONS`'s values are each section's *default* page — where it starts, and where
 * it falls back to if it was moved somewhere that no longer exists. Which page a section is
 * *currently* on is a separate, stored question — see "Section placement" below — because
 * unlike the arrangement (position within a page), placement (which page) is meant to
 * change: that is the whole point of being able to move a card at all.
 */
export const PAGE_SECTIONS: Record<PageId, SectionId[]> = {
  briefing: [
    "add",
    "activity",
    "wins",
    "notice",
    "gone-quiet",
    "first-run",
    "starred",
    "recurring",
    "inbox",
    "tasks",
    "notes",
    "blackjack",
  ],
  structure: ["areas", "projects"],
  settings: [
    "settings-display",
    "settings-capture",
    "settings-account",
    "settings-sessions",
    "settings-invites",
    "settings-tokens",
  ],
  models: ["models-context", "models-slots", "models-configs", "models-prompts"],
};

/**
 * Section placement — which pages each *portable* section currently appears on. Plural: a
 * card can be added to any number of pages at once rather than living on exactly one —
 * useful for the same card arranged differently on a wide-monitor page and a phone-width
 * one, say. Every self-contained card already fetches its own data independently (D102),
 * so two mounts of the same card have never disagreed about anything.
 *
 * `add`, `notice` and `first-run` used to be pinned here — `QuickAdd`'s own conjoined pieces,
 * unmovable, on the reasoning that a toast or an onboarding prompt means nothing without the
 * capture form beside it. D118 broke that assumption: each is now `postNotice`/its own fetch
 * rather than page-level state (`notices.ts`, `QuickAddCard`, `FirstRunCard`), so none of them
 * need a sibling to mean something — the whole reason anything was ever pinned. Nothing is,
 * now; `PINNED_SECTIONS` stays as the mechanism (below) in case a future card genuinely can't
 * stand alone, but it is empty.
 *
 * Stored separately from the arrangement (`efss.layout`), because the two answer different
 * questions and clearing one should not touch the other — "reset all" on a page's arrangement
 * re-centers its cards, it does not send away ones a reader deliberately added there from
 * somewhere else.
 */
export const PINNED_SECTIONS: readonly SectionId[] = [];

export function isPortable(id: SectionId): boolean {
  return !PINNED_SECTIONS.includes(id);
}

/** Every section id that exists, portable or not — derived so a new one only has to be
 * added to `SECTION_LABELS` to be known everywhere else. */
export const ALL_SECTIONS = Object.keys(SECTION_LABELS) as SectionId[];

export const PORTABLE_SECTIONS: readonly SectionId[] = ALL_SECTIONS.filter(isPortable);

/** The pages this build ships with, and the ids `routes.ts` gives fixed paths — `/`,
 * `/settings/structure`, `/settings`, `/models` render their component regardless of
 * whether the page list still has an entry for it (an old bookmark has to keep working).
 * Otherwise not special: `deletePage` will remove one like any other page someone created —
 * `/models` included, per D182 — at which point
 * it just stops appearing in the nav. */
export const BUILTIN_PAGES: readonly PageId[] = [
  "briefing",
  "structure",
  "settings",
  "models",
];

const BUILTIN_PAGE_NAMES: Record<string, string> = {
  briefing: "Briefing",
  structure: "Areas & projects",
  settings: "Settings",
  models: "AI",
};

/** The page a section starts on, and falls back to if moved somewhere no longer valid. */
export function defaultSectionPage(id: SectionId): PageId {
  for (const page of Object.keys(PAGE_SECTIONS) as PageId[]) {
    if (PAGE_SECTIONS[page].includes(id)) return page;
  }
  // Every known id is in PAGE_SECTIONS somewhere; this is unreached in practice.
  return "briefing";
}

/**
 * The page list — stage 4. A generated id and a name a reader picked, so pages beyond the
 * three built-in ones can exist at all. Stored separately from both the arrangement and
 * section placement, same "per device" reasoning, same reason it must not be disturbed by
 * clearing either of those: creating a page is a different act from arranging one.
 */
export type PageMeta = { id: PageId; name: string };

const PAGES_KEY = "efss.pages";

function defaultPages(): PageMeta[] {
  return BUILTIN_PAGES.map((id) => ({ id, name: BUILTIN_PAGE_NAMES[id] }));
}

function isPageMetaArray(value: unknown): value is PageMeta[] {
  return (
    Array.isArray(value) &&
    value.every(
      (p) =>
        p && typeof p === "object" && typeof p.id === "string" && typeof p.name === "string",
    )
  );
}

function readPages(): PageMeta[] {
  try {
    const raw = window.localStorage.getItem(PAGES_KEY);
    if (!raw) return defaultPages();
    const parsed: unknown = JSON.parse(raw);
    return isPageMetaArray(parsed) ? parsed : defaultPages();
  } catch {
    return defaultPages();
  }
}

function writePages(pages: PageMeta[]): void {
  try {
    window.localStorage.setItem(PAGES_KEY, JSON.stringify(pages));
  } catch {
    /* ignore — the page list simply will not persist */
  }
}

/** Every page that exists, built-in ones first in their fixed order, then custom ones in
 * the order they were created. */
export function listPages(): PageMeta[] {
  return readPages();
}

/** A page's name, or its own id if it has somehow gone missing from the stored list — a
 * broken label beats a broken render. */
export function pageName(id: PageId): string {
  return readPages().find((p) => p.id === id)?.name ?? id;
}

function generatePageId(): PageId {
  return `page-${Math.random().toString(36).slice(2, 10)}`;
}

/** Create a new, empty page. It starts with nothing on it — cards arrive by being moved
 * there (`moveSectionToPage`), the same way any portable section reaches any other page. */
export function createPage(name: string): PageMeta {
  const page: PageMeta = { id: generatePageId(), name: name.trim() || "New page" };
  writePages([...readPages(), page]);
  return page;
}

export function renamePage(id: PageId, name: string): void {
  const trimmed = name.trim();
  if (!trimmed) return;
  writePages(readPages().map((p) => (p.id === id ? { ...p, name: trimmed } : p)));
}

/**
 * Remove a page — any page, built-in or custom. Every portable section that was only here
 * recovers automatically (`sectionPages`'s own "every page it was on is gone" fallback), so
 * there is nothing to migrate by hand. Also clears the deleted page's own arrangement,
 * widths and column override, which could otherwise never be reached or cleaned up again.
 *
 * Never leaves zero pages: deleting the last one creates a fresh, blank replacement instead
 * — there always has to be somewhere to land, and a blank page is a better empty state than
 * no page at all.
 */
export function deletePage(id: PageId): void {
  const remaining = readPages().filter((p) => p.id !== id);
  writePages(remaining.length > 0 ? remaining : [{ id: generatePageId(), name: "New page" }]);
  clearArrangement(id);
}

/**
 * Put the page list back to exactly the three built-ins, discarding any custom pages —
 * "reset all"'s global counterpart to `clearSectionPlacement`. Without this, resetting
 * placement to defaults after every custom page has been deleted did nothing visible: the
 * defaults are all built-in-page ids, but the built-ins themselves were still gone, so there
 * was nowhere for anything to land back on.
 *
 * Deliberately as blunt as `clearSectionPlacement` — a custom page left behind after a
 * global reset would be permanently empty (every section that could have been on it just
 * went back to its default page instead), which is a worse outcome than removing it.
 */
export function resetPages(): void {
  writePages(defaultPages());
}

const PLACEMENT_KEY = "efss.section-placement";

type Placement = Partial<Record<SectionId, PageId[]>>;

function isPageIdArray(value: unknown): value is PageId[] {
  return Array.isArray(value) && value.every((v) => typeof v === "string");
}

function readPlacement(): Placement {
  try {
    const raw = window.localStorage.getItem(PLACEMENT_KEY);
    if (!raw) return {};
    const parsed = JSON.parse(raw) as Record<string, unknown>;
    const result: Placement = {};
    for (const [id, value] of Object.entries(parsed)) {
      // The pre-multi-page shape stored one bare page id per section; still read as a
      // single-entry list, so an existing placement survives moving to this shape.
      if (typeof value === "string") result[id as SectionId] = [value];
      else if (isPageIdArray(value)) result[id as SectionId] = value;
    }
    return result;
  } catch {
    return {};
  }
}

function writePlacement(placement: Placement): void {
  try {
    window.localStorage.setItem(PLACEMENT_KEY, JSON.stringify(placement));
  } catch {
    /* ignore — the placement simply will not persist */
  }
}

/**
 * Every page a section currently appears on.
 *
 * A pinned one always answers with just its default — it was never offered a move. A
 * portable one answers with wherever it has explicitly been added, filtered to pages that
 * still exist. Three different-looking empty cases resolve three different ways, on
 * purpose: never touched at all falls back to the default page; explicitly removed from
 * every page it was on (`removeSectionFromPage` writes that as a real, empty list) stays
 * truly nowhere, so it doesn't reappear somewhere unasked-for; every page it *was* on having
 * since been deleted recovers onto whatever page happens to be first, so a section can never
 * end up permanently unreachable through no choice of its own.
 */
export function sectionPages(
  id: SectionId,
  validPages: readonly PageId[] = listPages().map((p) => p.id),
): PageId[] {
  if (!isPortable(id)) return [defaultSectionPage(id)];

  const stored = readPlacement()[id];
  if (stored === undefined) return [defaultSectionPage(id)];

  const filtered = stored.filter((page) => validPages.includes(page));
  if (filtered.length > 0) return filtered;
  if (stored.length === 0) return [];
  return validPages.length > 0 ? [validPages[0]] : [];
}

export function isSectionOnPage(
  id: SectionId,
  page: PageId,
  validPages: readonly PageId[] = listPages().map((p) => p.id),
): boolean {
  return sectionPages(id, validPages).includes(page);
}

/** Every section — pinned or portable — currently on this page, for however a caller wants
 * to enumerate "everything that could be here" (`LayoutGrid`'s arrange-mode placeholders,
 * a page building its own section list). */
export function sectionsForPage(
  page: PageId,
  validPages: readonly PageId[] = listPages().map((p) => p.id),
  known: readonly SectionId[] = ALL_SECTIONS,
): SectionId[] {
  return known.filter((id) => isSectionOnPage(id, page, validPages));
}

/**
 * Add a portable section to a page, alongside anywhere it already appears — this is what
 * lets the same card show up on more than one page. A no-op if it's already there, or on a
 * pinned id (nothing in the UI can call it with one either — `AddSectionPicker` only ever
 * offers portable ids).
 */
export function addSectionToPage(id: SectionId, page: PageId): void {
  if (!isPortable(id)) return;
  const current = sectionPages(id);
  if (current.includes(page)) return;
  writePlacement({ ...readPlacement(), [id]: [...current, page] });
}

/**
 * Remove a portable section from one page, without touching anywhere else it appears. Can
 * leave it on no page at all — deliberately: `sectionPages` treats "nowhere" as its own
 * state rather than snapping back to a default, so a card taken off its last page stays
 * removed instead of reappearing somewhere unasked-for. `CardsPicker` still offers it on
 * every page in that state, so it's never lost, only unplaced. A no-op on a pinned id.
 *
 * Removes from `sectionPages(id)` — the *resolved* set a checkbox actually shows as checked
 * — not the raw stored array. Those two can differ: a section recovered onto this page
 * because every page it was on got deleted (`sectionPages`'s own fallback) is checked here
 * without `page` ever literally being in the stored list. Filtering the raw list for `page`
 * in that case removes nothing — `page` was never in it — so the write is a no-op and the
 * checkbox snaps back the moment something re-reads it: unchecking looked like it did
 * nothing, because it did. Filtering the resolved list turns "recovered here" into "written
 * here explicitly, then removed," which actually changes what gets stored.
 */
export function removeSectionFromPage(id: SectionId, page: PageId): void {
  if (!isPortable(id)) return;
  const current = sectionPages(id);
  writePlacement({ ...readPlacement(), [id]: current.filter((p) => p !== page) });
}

/** Columns of section ids, outermost index is the column. */
export type Arrangement = SectionId[][];

/**
 * One page's layout: which cards sit in which column, and how wide the columns are. Both
 * keyed by how many columns are being shown, for the reason in the module docstring.
 */
export type PageLayout = Partial<Record<ColumnCount, Arrangement>> & {
  widths?: Partial<Record<ColumnCount, number[]>>;
  /**
   * A hand-set column count. Three states, not two: the key being *absent* means "never
   * chosen, use this page's own default"; an explicit `null` means "chose auto on purpose,
   * stay on auto"; a number means "chose this count on purpose". Collapsing the last two
   * into one — which deleting the key on "auto" used to do — makes a deliberate choice of
   * auto indistinguishable from never having chosen anything, so a page whose default isn't
   * auto (Structure, Settings) would silently un-choose auto the next time it loaded.
   */
  columns?: number | null;
};

/**
 * A generated string for a custom page, or one of the three built-in ids. Was a closed
 * union of the three; widened for stage 4's stored, editable page list — a page can now be
 * something a reader created, so nothing that reads a `PageId` can assume it is one of a
 * fixed set anymore. `BUILTIN_PAGES` is what "the fixed set" still means, for the code that
 * genuinely needs it — `routes.ts`'s fixed paths, and the default pages a fresh install
 * seeds `efss.pages` with.
 */
export type PageId = string;

/**
 * The defaults, as specified by the owner.
 *
 * Column 1 is the working strip: capture, what has gone quiet, then everything open.
 * Column 2 is the day you have chosen — wins, the starred hit list, the inbox waiting to be
 * filed, and the game. Starred belongs here rather than with the open tasks: it is what you
 * picked for today, which is a decision already made, not a list to work through.
 *
 * §8.2's reading order is preserved in the one-column case, which is what a phone gets, so
 * the spec's order is still the order on the device the spec was written about.
 */
const BRIEFING_DEFAULTS: Record<ColumnCount, Arrangement> = {
  1: [
    [
      "add",
      "activity",
      "notice",
      "wins",
      "gone-quiet",
      "first-run",
      "starred",
      "recurring",
      "inbox",
      "tasks",
      "notes",
      "blackjack",
    ],
  ],
  2: [
    ["add", "gone-quiet", "tasks"],
    ["activity", "first-run", "notice", "wins", "starred", "recurring", "inbox", "notes", "blackjack"],
  ],
  3: [
    ["add", "gone-quiet", "tasks"],
    ["activity", "wins", "starred", "recurring", "inbox"],
    ["first-run", "notice", "notes", "blackjack"],
  ],
};

const STRUCTURE_DEFAULTS: Record<ColumnCount, Arrangement> = {
  1: [["areas", "projects"]],
  2: [["areas"], ["projects"]],
  3: [["areas"], ["projects"], []],
};

const SETTINGS_DEFAULTS: Record<ColumnCount, Arrangement> = {
  1: [
    [
      "settings-display",
      "settings-capture",
      "settings-account",
      "settings-sessions",
      "settings-invites",
      "settings-tokens",
    ],
  ],
  2: [
    ["settings-display", "settings-capture", "settings-account"],
    ["settings-sessions", "settings-invites", "settings-tokens"],
  ],
  3: [
    ["settings-display", "settings-capture"],
    ["settings-account", "settings-sessions"],
    ["settings-invites", "settings-tokens"],
  ],
};

const MODELS_DEFAULTS: Record<ColumnCount, Arrangement> = {
  1: [["models-context", "models-slots", "models-configs", "models-prompts"]],
  2: [["models-context"], ["models-slots", "models-configs", "models-prompts"]],
  3: [["models-context"], ["models-slots", "models-configs"], ["models-prompts"]],
};

// Partial, not `Record<PageId, ...>`: a custom page has no entry, and has none on purpose —
// see `defaultArrangement`'s fallback for what that means for it.
const DEFAULTS: Partial<Record<PageId, Record<ColumnCount, Arrangement>>> = {
  briefing: BRIEFING_DEFAULTS,
  structure: STRUCTURE_DEFAULTS,
  settings: SETTINGS_DEFAULTS,
  models: MODELS_DEFAULTS,
};

/** The most columns the layout has a considered opinion about. Beyond this it pads. */
const DESIGNED_FOR = 3;

export function defaultArrangement(page: PageId, columns: ColumnCount): Arrangement {
  const wanted = Math.max(Math.floor(columns), 1);
  // A custom page has no considered arrangement — nothing starts there by default, only by
  // being moved — so it gets the same treatment `DESIGNED_FOR` already gives a fourth
  // column on a built-in page: empty, a drop target, not a rule nobody chose.
  const designed = DEFAULTS[page]?.[Math.min(wanted, DESIGNED_FOR)] ?? [];
  const base = designed.map((column) => [...column]);

  // Past three there is no considered arrangement, so extra columns arrive empty rather
  // than being filled by a rule nobody chose. They are drop targets; put what you want there.
  while (base.length < wanted) base.push([]);
  return base.slice(0, wanted);
}

const STORAGE_KEY = "efss.layout";

type Stored = Partial<Record<PageId, PageLayout>>;

function read(): Stored {
  try {
    const raw = window.localStorage.getItem(STORAGE_KEY);
    return raw ? (JSON.parse(raw) as Stored) : {};
  } catch {
    // Private browsing, disabled storage, or something else's key collided and left junk.
    // A default layout is a perfectly good outcome; a crashed briefing is not.
    return {};
  }
}

function write(stored: Stored): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, JSON.stringify(stored));
  } catch {
    /* ignore — the arrangement simply will not persist */
  }
}

/**
 * The saved arrangement, reconciled against the sections that actually exist.
 *
 * Three things drift. Sections get removed, leaving ids in storage that no longer render;
 * sections get *added* — Phase 2 will bring more — which a saved layout has never heard of;
 * and most of the briefing's cards are conditional, so on any given morning half the
 * defaults name nothing.
 *
 * **The result contains only cards that will render.** That is not tidiness. A card's
 * position in this array is its `data-index`, which is what the drag arithmetic and the
 * keyboard moves are computed from — so an id in the array that draws nothing puts a phantom
 * entry between two real cards, and every move near it lands one place off. It presents as
 * "the arrow keys sometimes do nothing", which is a miserable thing to debug.
 *
 * An unknown id is dropped; a missing one is appended to the column its *default* puts it
 * in, so a new card appears somewhere deliberate rather than at the top of column one.
 */
export function reconcile(
  saved: Arrangement | undefined,
  page: PageId,
  columns: ColumnCount,
  known: readonly SectionId[],
): Arrangement {
  const valid = new Set(known);
  const defaults = defaultArrangement(page, columns);

  // `seen` also dedupes: a corrupted or hand-edited entry naming the same card twice would
  // otherwise render two cards sharing a React key, and the second one silently wins.
  const source = saved ?? defaults;
  const seen = new Set<SectionId>();
  const result: Arrangement = Array.from({ length: columns }, (_, index) =>
    (source[index] ?? []).filter((id): id is SectionId => {
      if (!valid.has(id) || seen.has(id)) return false;
      seen.add(id);
      return true;
    }),
  );

  for (const id of known) {
    if (seen.has(id)) continue;
    const home = defaults.findIndex((column) => column.includes(id));
    result[home === -1 ? 0 : Math.min(home, columns - 1)].push(id);
    seen.add(id);
  }

  return result;
}

export function loadArrangement(
  page: PageId,
  columns: ColumnCount,
  known: readonly SectionId[],
): Arrangement {
  return reconcile(read()[page]?.[columns], page, columns, known);
}

export function saveArrangement(
  page: PageId,
  columns: ColumnCount,
  arrangement: Arrangement,
): void {
  const stored = read();
  write({ ...stored, [page]: { ...stored[page], [columns]: arrangement } });
}

/**
 * Column widths, as fractions summing to the column count — or, once an outer edge has
 * been dragged, `[margin, col1, ..., colN, margin]`, two entries longer.
 *
 * Fractions rather than pixels so a stored width still means something after the window
 * changes size — `[1.4, 0.6]` is "seventy/thirty" at any width, where `[700, 300]` is a lie
 * the moment you resize. Summing to the count rather than to 1 means the untouched default
 * is `[1, 1, 1]`, which is also a valid `grid-template-columns`.
 *
 * ## Two shapes, on purpose
 *
 * A bare `length === columns` array is "cap mode": the grid renders only the real columns
 * and lets `.layout-grid`'s own CSS (`max-width: calc(columns * 44rem)`, `margin-inline:
 * auto`) supply the outer margin and center it — exactly what reset, auto, and a freshly
 * chosen column count all produce, unchanged.
 *
 * A `length === columns + 2` array is "explicit mode": the margins are themselves fr-tracks
 * the reader dragged into existence, so the CSS cap is suppressed (`.explicit-widths`) and
 * `resizeColumns` treats a margin exactly like a column — it doesn't know the difference.
 * Nothing produces this shape except dragging an outer edge; `seedExplicitWidths` is the
 * one-time conversion at the moment that first happens.
 */
export type Widths = number[];

/** Never let a real column be dragged narrower than this share of an even split. */
const MIN_SHARE = 0.35;

export function defaultWidths(columns: ColumnCount): Widths {
  return Array.from({ length: columns }, () => 1);
}

export function loadWidths(page: PageId, columns: ColumnCount): Widths {
  const saved = read()[page]?.widths?.[columns];
  const validLength = saved?.length === columns || saved?.length === columns + 2;
  if (!saved || !validLength || saved.some((n) => !Number.isFinite(n) || n <= 0)) {
    return defaultWidths(columns);
  }
  return saved;
}

/**
 * Turn the current on-screen geometry into an explicit `[margin, col1, ..., colN, margin]`
 * array, so dragging an outer edge for the first time starts from what is already on
 * screen rather than jumping. A no-op once already in that shape.
 *
 * `.layout-grid`'s width is whatever cap mode's CSS currently renders it at (the `44rem` /
 * `56.25rem` cap, or the full measured width if the columns are wider than that); the gap
 * between it and the measuring wrapper is split evenly, matching `margin-inline: auto`.
 */
export function seedExplicitWidths(
  widths: Widths,
  columns: ColumnCount,
  measure: HTMLElement | null,
): Widths {
  if (widths.length === columns + 2) return widths;

  const measureWidth = measure?.getBoundingClientRect().width;
  const gridWidth = measure
    ?.querySelector<HTMLElement>(".layout-grid")
    ?.getBoundingClientRect().width;

  if (!measureWidth || !gridWidth) return [1, ...widths, 1];

  const totalFr = widths.reduce((sum, w) => sum + w, 0);
  const pxPerFr = gridWidth / totalFr;
  const marginFr = pxPerFr > 0 ? Math.max(measureWidth - gridWidth, 0) / 2 / pxPerFr : 1;

  return [marginFr, ...widths, marginFr];
}

export function saveWidths(page: PageId, columns: ColumnCount, widths: Widths): void {
  const stored = read();
  const page_ = stored[page] ?? {};
  write({
    ...stored,
    [page]: { ...page_, widths: { ...page_.widths, [columns]: widths } },
  });
}

/**
 * Move the boundary between two neighbouring tracks by a fraction of the container.
 *
 * Only the pair either side of the gutter changes, so dragging one boundary cannot ripple
 * through the whole row — the columns you already settled stay where you put them. Works
 * identically whether both sides are real columns or one is a margin — this function does
 * not know which; the caller picks `minLeft`/`minRight` for that. A real column keeps
 * `MIN_SHARE` so it can never be dragged to nothing; a margin passes `0` on its own side so
 * it can collapse fully, e.g. for an edge-to-edge layout.
 */
export function resizeColumns(
  widths: Widths,
  boundary: number,
  deltaFraction: number,
  minLeft: number = MIN_SHARE,
  minRight: number = MIN_SHARE,
): Widths {
  const next = [...widths];
  const left = next[boundary];
  const right = next[boundary + 1];
  if (left === undefined || right === undefined) return widths;

  const total = left + right;
  // `deltaFraction` is a fraction of the *container's pixel width*; to turn that into a
  // change in `left`'s own share, scale it by the sum every share is drawn against — not
  // `next.length`. The two agree in cap mode, where `defaultWidths` seeds exactly `1` per
  // column and every resize preserves the pair's own total, so the array's sum always
  // equals its length. They part ways the moment an edge is dragged: `seedExplicitWidths`
  // appends two margin tracks sized from real on-screen pixels, almost never `1` each (a
  // margin is usually a fraction of a full column), so the array grew by 2 in length but by
  // some other amount in sum. Scaling by `next.length` there overstated the true total,
  // and the boundary moved faster than the pointer that was dragging it — reported directly.
  const totalUnits = next.reduce((sum, width) => sum + width, 0);
  const wanted = left + deltaFraction * totalUnits;
  const clamped = Math.max(minLeft, Math.min(wanted, total - minRight));

  next[boundary] = clamped;
  next[boundary + 1] = total - clamped;
  return next;
}

/**
 * What each page assumes until the reader picks a count for themselves.
 *
 * Briefing's cards are square enough that following the window width always looks
 * reasonable, so it gets no fixed opinion. Structure is a pair of tables that read better
 * side by side than stacked under a phone-width single column, and Settings is prose end to
 * end — one column, like `.app`'s own reading measure, is the whole point. A custom page's
 * content is whatever got moved there, so it gets Briefing's answer — no fixed opinion — for
 * the same reason.
 */
const DEFAULT_COLUMNS: Partial<Record<PageId, number | null>> = {
  briefing: null,
  structure: 2,
  settings: 1,
  models: 1,
};

/**
 * The hand-set column count, or the page's own default if nothing has been set.
 *
 * Stored per page and per device alongside the arrangement, for the same reason: how many
 * columns you want is an opinion about the screen in front of you.
 *
 * The `columns` key being absent entirely — as opposed to present and `null` — is what
 * separates "never chosen" from "chose auto on purpose". Only the former falls back to
 * `DEFAULT_COLUMNS`; a deliberate choice of auto has to stay auto on a page whose default
 * isn't, or choosing it would be indistinguishable from never touching the control at all.
 */
export function loadColumnOverride(page: PageId): number | null {
  const stored = read()[page];
  if (!stored || !("columns" in stored)) return DEFAULT_COLUMNS[page] ?? null;

  const saved = stored.columns;
  return typeof saved === "number" && saved >= 1 ? Math.floor(saved) : null;
}

export function saveColumnOverride(page: PageId, columns: number | null): void {
  const stored = read();
  // Stored as a real `null`, not deleted — see `PageLayout.columns` and `loadColumnOverride`
  // for why the key must stay present to record "chose auto" rather than "chose nothing".
  write({ ...stored, [page]: { ...(stored[page] ?? {}), columns } });
}

export function clearArrangement(page: PageId): void {
  const stored = read();
  delete stored[page];
  write(stored);
}

/**
 * Put every portable section back on its default page, everywhere at once — unlike
 * `clearArrangement`, which only ever affects the one page it's given. "Reset all" resets
 * this page's own arrangement and, on top of that, every page's card placement in one go:
 * two different stores, two different scopes, one button, because from the reader's side
 * "put every card back where it started" doesn't distinguish the two.
 */
export function clearSectionPlacement(): void {
  try {
    window.localStorage.removeItem(PLACEMENT_KEY);
  } catch {
    /* ignore — the placement simply keeps whatever it already had */
  }
}

/**
 * Move a card to a column and a gap within it.
 *
 * `gap` is numbered the way `useRowReorder` numbers it — 0 is above the first card, and the
 * column's length is below the last. Removing the card before inserting means a move within
 * one column has to account for the hole it left, which is the off-by-one every
 * drag-to-reorder gets wrong once.
 */
export function moveSection(
  arrangement: Arrangement,
  id: SectionId,
  toColumn: number,
  gap: number,
): Arrangement {
  const next = arrangement.map((column) => column.filter((each) => each !== id));
  const fromColumn = arrangement.findIndex((column) => column.includes(id));
  const fromIndex = fromColumn === -1 ? -1 : arrangement[fromColumn].indexOf(id);

  const target = Math.max(0, Math.min(toColumn, next.length - 1));
  const shift = fromColumn === target && fromIndex !== -1 && fromIndex < gap ? 1 : 0;
  const at = Math.max(0, Math.min(gap - shift, next[target].length));

  next[target].splice(at, 0, id);
  return next;
}
